import type { FinalRankingItem } from "../types";

type RankingAppDependencies = {
  // 静的アーカイブ化のため、ランキングはビルド時に同梱した静的データを使う (#630)
  data: FinalRankingItem[];
};

const getTitleFromRank = (rank: number): string => {
  switch (rank) {
    case 1:
      return "金賞";
    case 2:
      return "銀賞";
    case 3:
      return "銅賞";
    case 4:
      return "若手<br/>特別賞";
    default:
      return "";
  }
};

function createRankMemberElement(item: FinalRankingItem): HTMLDivElement {
  const html = `
    <div class="numbering">${getTitleFromRank(item.rank)}</div>
    <div class="rank-info">
      <div class="organization-name${item.organizationName ? "" : " private"}">${item.organizationName || "非公開"}</div>
      <div class="member-info">
        <span class="nickname">${item.displayName}</span>
        <div class="age">${item.age}</div>
      </div>
      <div class="rank-bottom" style="display: flex; justify-content: space-between; align-items: center;">
        <div class="name${item.name ? "" : " private"}" style="font-size: 19px;">${item.name || "非公開"}</div>
        <div class="score">
          <span>${item.score}</span><span>点</span>
        </div>
      </div>
    </div>
  `;
  const root = document.createElement("div");
  root.className = "rank-member final";
  root.setAttribute("data-display-name", item.displayName);
  root.innerHTML = html.trim();
  if (item.rank === 1) {
    root.classList.add("rank-1");
  } else if (item.rank === 2) {
    root.classList.add("rank-2");
  } else if (item.rank === 3) {
    root.classList.add("rank-3");
  } else if (item.rank === 4) {
    root.classList.add("rank-young");
  }
  const nicknameElement = root.querySelector(".nickname");
  if (nicknameElement) {
    nicknameElement.innerHTML = item.displayName;
  }
  const ageElement = root.querySelector(".age");
  if (ageElement) {
    if (item.age) {
      ageElement.innerHTML = `
      <span>(</span><span>${item.age}</span><span class="age-kanji">歳</span><span>)</span>`;
    } else {
      ageElement.parentNode?.removeChild(ageElement);
    }
  }
  return root as HTMLDivElement;
}

async function renderRanking(listRoot: HTMLElement, data: FinalRankingItem[]) {
  listRoot.innerHTML = "";

  const fragment = document.createDocumentFragment();
  for (const item of data) {
    fragment.appendChild(createRankMemberElement(item));
  }
  listRoot.appendChild(fragment);
}

function getDisplayNameFromHash(): string | null {
  const prefix = "#rank-user-";
  if (!location.hash || !location.hash.startsWith(prefix)) return null;
  const encoded = location.hash.substring(prefix.length);
  try {
    return decodeURIComponent(encoded);
  } catch (_) {
    return encoded;
  }
}

function openFromHashIfPresent(rankListSection: HTMLElement, examName: string) {
  const displayNameFromHash = getDisplayNameFromHash();
  if (!displayNameFromHash) return;
  const member = Array.from(
    rankListSection.querySelectorAll<HTMLElement>(".rank-member"),
  ).find((el) => el.getAttribute("data-display-name") === displayNameFromHash);
  if (member) openRankModalWith(member, examName);
}

function ensureRankModal() {
  let modal = document.getElementById("rankMemberModal");
  if (modal) return modal as HTMLDivElement;

  modal = document.createElement("div");
  modal.id = "rankMemberModal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");

  const overlay = document.createElement("div");
  overlay.className = "rank-modal-overlay";

  const close = document.createElement("span");
  close.className = "rank-modal-close";
  close.textContent = "×";

  const content = document.createElement("div");
  content.className = "rank-modal-content";

  modal.appendChild(overlay);
  modal.appendChild(close);
  modal.appendChild(content);
  document.body.appendChild(modal);

  return modal as HTMLDivElement;
}

function openRankModalWith(member: HTMLElement, examName: string) {
  const modal = ensureRankModal();
  const content = modal.querySelector(
    ".rank-modal-content",
  ) as HTMLDivElement | null;
  if (!content) return;

  // Clear previous content
  content.innerHTML = "";

  // Wrap with a list container so existing CSS applies
  const wrapper = document.createElement("div");
  wrapper.className = "rank-member-list";

  // Clone the clicked rank member and append
  const cloned = member.cloneNode(true) as HTMLElement;
  wrapper.appendChild(cloned);
  content.appendChild(wrapper);

  // Add a share link for X (Twitter) under the list
  try {
    const rankText =
      (cloned.querySelector(".numbering") as HTMLElement | null)?.textContent ??
      "";
    const scoreText =
      (
        cloned.querySelector(".score span") as HTMLElement | null
      )?.textContent?.trim() ?? "";
    const score = Number.parseInt(scoreText, 10);
    // Build share URL. Ensure hash identifies the user even if not yet set.
    const displayName = member.getAttribute("data-display-name");
    const computedHash = displayName
      ? `#rank-user-${encodeURIComponent(displayName)}`
      : location.hash;
    const currentUrl = `${location.origin}${location.pathname}${location.search}${computedHash ?? ""}`;
    const tweetText = `${examName}の結果は${rankText}、${
      Number.isNaN(score) ? "?" : score
    }点でした。\n\n`;
    const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      currentUrl,
    )}&text=${encodeURIComponent(tweetText)}`;

    const shareContainer = document.createElement("div");
    shareContainer.className = "rank-share";
    const shareLink = document.createElement("a");
    shareLink.href = tweetUrl;
    shareLink.target = "_blank";
    shareLink.rel = "noopener noreferrer";
    shareLink.textContent = "Xでシェア";
    shareContainer.appendChild(shareLink);
    content.appendChild(shareContainer);
  } catch (_) {
    // ignore parsing issues, do not block modal
  }

  // Show modal and lock background scroll
  modal.classList.add("active");
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  // Close handlers
  const overlay = modal.querySelector(".rank-modal-overlay") as HTMLElement;
  const closeBtn = modal.querySelector(".rank-modal-close") as HTMLElement;

  const handleClose = () => {
    modal.classList.remove("active");
    document.body.style.overflow = prevOverflow;
    // Remove hash if it was set for rank modal
    if (location.hash?.startsWith("#rank-user-")) {
      const urlWithoutHash = location.pathname + location.search;
      history.replaceState(null, "", urlWithoutHash);
    }
    // Cleanup listeners
    overlay.removeEventListener("click", handleClose);
    closeBtn.removeEventListener("click", handleClose);
    document.removeEventListener("keydown", onKeyDown);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") handleClose();
  };

  overlay.addEventListener("click", handleClose);
  closeBtn.addEventListener("click", handleClose);
  document.addEventListener("keydown", onKeyDown);
}

function setupRankMemberModal(rankListSection: HTMLElement, examName: string) {
  // Delegate click events to open modal with the clicked item
  rankListSection.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const member = target.closest(".rank-member") as HTMLElement | null;
    if (!member) return;
    openRankModalWith(member, examName);
    const displayName = member.getAttribute("data-display-name");
    if (displayName) {
      const hash = `rank-user-${encodeURIComponent(displayName)}`;
      if (location.hash !== `#${hash}`) {
        location.hash = hash;
      }
    }
  });
}

export async function installFinalRankApp(
  dependencies: RankingAppDependencies,
) {
  const { data } = dependencies;
  const section = document.querySelector(
    "section.ranking-section",
  ) as HTMLElement | null;
  if (!section) return;
  const listElement = section.querySelector(
    ".rank-member-list",
  ) as HTMLElement | null;
  if (!listElement) return;

  const examId = section.getAttribute("data-exam-id");
  const examLongName = (
    section.getAttribute("data-exam-long-name") || ""
  ).replace(/\\n/g, "\n");
  if (!examId || !examLongName) return;

  if (data.length === 0) {
    // No ranking data to show
    return;
  }
  section.classList.remove("hidden");
  await renderRanking(listElement, data);
  setupRankMemberModal(listElement, examLongName);

  // Open modal on initial load after data rendering
  openFromHashIfPresent(listElement, examLongName);
}
